
import React, { useEffect, useState } from 'react';
import { ICONS } from '../constants';
import { auth, getUserDocument, updateUserDocument, deleteUserDocument, updateUserProfile, logoutUser, createPromoCode, toggleAdminStatus } from '../services/firebase';
import { syncLocalToCloud, getHistoryItems } from '../services/storageService';
import { LegalModal } from './LegalDocs';
import { SupportModal } from './SupportModal';

interface ProfileProps {
  onOpenSubscription?: () => void;
}

const Profile: React.FC<ProfileProps> = ({ onOpenSubscription }) => {
  const [userData, setUserData] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [imageCount, setImageCount] = useState(0);

  // Legal Modal State
  const [activeLegalModal, setActiveLegalModal] = useState<'TERMS' | 'PRIVACY' | null>(null);
  
  // Support Modal State
  const [showSupport, setShowSupport] = useState(false);

  // Form State
  const [editName, setEditName] = useState('');
  const [editPhoto, setEditPhoto] = useState('');

  // Admin Promo State
  const [newPromoCode, setNewPromoCode] = useState('RESHOOT20');
  const [newPromoValue, setNewPromoValue] = useState(20);
  const [creatingPromo, setCreatingPromo] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    if (auth?.currentUser) {
        const uid = auth.currentUser.uid;
        const data = await getUserDocument(uid);
        if (data) {
            setUserData(data);
            setEditName(data.name || '');
            setEditPhoto(data.photoFileName || '');
        }
        // Fetch accurate history count
        const history = await getHistoryItems(uid);
        setImageCount(history.length);
    }
    setLoading(false);
  };

  const handleSave = async () => {
      if (!auth?.currentUser) return;
      setSaving(true);
      try {
          // Update Firestore
          await updateUserDocument(auth.currentUser.uid, {
              name: editName,
              photoFileName: editPhoto
          });
          
          // Update Auth Profile (Display Name)
          await updateUserProfile(auth.currentUser, editName);
          
          setUserData({ ...userData, name: editName, photoFileName: editPhoto });
          setIsEditing(false);
      } catch (error) {
          console.error("Failed to update profile", error);
          alert("Failed to update profile. Please try again.");
      } finally {
          setSaving(false);
      }
  };

  const handleDeleteAccount = async () => {
      const confirmed = window.confirm("Are you sure you want to delete your account? This action is irreversible.");
      if (confirmed && auth?.currentUser) {
          try {
              const uid = auth.currentUser.uid;
              // 1. Delete Firestore Doc
              await deleteUserDocument(uid);
              // 2. Delete Auth User
              await auth.currentUser.delete();
          } catch (error) {
              console.error("Delete failed", error);
              alert("Failed to delete account. You may need to sign in again to verify identity.");
          }
      }
  };

  const handleCreatePromo = async (e: React.FormEvent) => {
      e.preventDefault();
      setCreatingPromo(true);
      try {
          await createPromoCode(newPromoCode, newPromoValue);
          alert(`Success! Code ${newPromoCode} created with ${newPromoValue} RESHOOTS.`);
          setNewPromoCode('');
      } catch (e: any) {
          alert(`Error: ${e.message}. Ensure you have admin permissions.`);
      } finally {
          setCreatingPromo(false);
      }
  };

  const toggleMyAdminStatus = async () => {
      if (!auth?.currentUser) return;
      const newStatus = !userData?.isAdmin;
      await toggleAdminStatus(auth.currentUser.uid, newStatus);
      await fetchProfile(); // refresh
  };

  if (loading) {
      return (
          <div className="flex-1 bg-surface flex items-center justify-center">
              <ICONS.Refresh className="w-6 h-6 animate-spin text-zinc-500" />
          </div>
      );
  }

  return (
    <div className="flex-1 bg-surface relative overflow-y-auto">
       <div className="absolute inset-0 pattern-grid opacity-5 pointer-events-none fixed"></div>
       
       <div className="max-w-3xl mx-auto px-6 py-12 relative z-10 pb-32">
           
           {/* Header */}
           <header className="flex flex-col md:flex-row md:items-center gap-6 mb-12 border-b border-white/5 pb-8">
               <div className="w-24 h-24 bg-gradient-to-br from-zinc-700 to-zinc-900 rounded-full border-2 border-white/10 flex items-center justify-center shadow-2xl overflow-hidden relative group">
                   {userData?.photoFileName ? (
                       <img src={userData.photoFileName} alt="Profile" className="w-full h-full object-cover" onError={(e) => e.currentTarget.style.display='none'} />
                   ) : (
                       <ICONS.User className="w-10 h-10 text-zinc-400" />
                   )}
               </div>
               
               <div className="flex-1">
                   {isEditing ? (
                       <div className="space-y-4 max-w-md">
                           <div>
                               <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Display Name</label>
                               <input 
                                   value={editName}
                                   onChange={(e) => setEditName(e.target.value)}
                                   className="w-full bg-black/40 border border-zinc-700 p-2 rounded text-white text-lg font-light focus:outline-none focus:border-white/50"
                               />
                           </div>
                           <div>
                               <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Photo URL</label>
                               <input 
                                   value={editPhoto}
                                   onChange={(e) => setEditPhoto(e.target.value)}
                                   className="w-full bg-black/40 border border-zinc-700 p-2 rounded text-white text-xs font-mono focus:outline-none focus:border-white/50"
                                   placeholder="https://..."
                               />
                           </div>
                           <div className="flex gap-3 mt-4">
                               <button 
                                   onClick={handleSave} 
                                   disabled={saving}
                                   className="px-4 py-2 bg-white text-black text-xs font-bold uppercase tracking-widest hover:bg-zinc-200 disabled:opacity-50"
                               >
                                   {saving ? 'Saving...' : 'Save Changes'}
                               </button>
                               <button 
                                   onClick={() => { setIsEditing(false); setEditName(userData.name); setEditPhoto(userData.photoFileName); }}
                                   className="px-4 py-2 border border-zinc-700 text-zinc-400 text-xs font-bold uppercase tracking-widest hover:text-white hover:border-white"
                               >
                                   Cancel
                               </button>
                           </div>
                       </div>
                   ) : (
                       <>
                           <h1 className="text-3xl font-light text-white mb-2">{userData?.name || 'Anonymous User'}</h1>
                           <div className="flex flex-col md:flex-row md:items-center gap-3">
                               <span className="px-2 py-0.5 bg-zinc-800 text-zinc-300 text-[10px] font-bold uppercase tracking-wider rounded-sm w-fit">
                                   {userData?.plan === 'pro' || userData?.plan === 'gold' ? 'Pro Plan' : userData?.plan === 'creator' || userData?.plan === 'silver' || userData?.plan === 'basic' ? 'Creator Plan' : 'TRIAL Plan'}
                               </span>
                               {userData?.isAdmin && (
                                   <span className="px-2 py-0.5 bg-red-900/50 text-red-400 text-[10px] font-bold uppercase tracking-wider rounded-sm w-fit border border-red-900/50">
                                       ADMIN
                                   </span>
                               )}
                               <span className="text-zinc-500 text-sm font-mono">{userData?.email}</span>
                           </div>
                           <button 
                               onClick={() => setIsEditing(true)}
                               className="mt-4 text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white flex items-center gap-2 border border-zinc-800 px-3 py-1.5 rounded-full hover:bg-white/5 transition-colors w-fit"
                           >
                               <ICONS.Settings className="w-3 h-3" /> Edit Profile
                           </button>
                       </>
                   )}
               </div>
           </header>

           {/* Stats Section */}
           <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
               {/* Stat Card 1 */}
               <div className="bg-black/40 border border-white/5 p-6 rounded-xl backdrop-blur-sm">
                   <div className="flex items-center justify-between mb-4">
                       <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-widest">Images Created</h3>
                       <ICONS.Camera className="w-4 h-4 text-white" />
                   </div>
                   <p className="text-4xl font-light text-white">{imageCount.toLocaleString()}</p>
               </div>
               
               {/* Stat Card 2 */}
               <div className="bg-black/40 border border-white/5 p-6 rounded-xl backdrop-blur-sm">
                   <div className="flex items-center justify-between mb-4">
                       <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-widest">Member Since</h3>
                       <ICONS.Star className="w-4 h-4 text-white" />
                   </div>
                   <p className="text-2xl font-light text-white">
                       {userData?.createdAt ? new Date(userData.createdAt.seconds * 1000).toLocaleDateString() : 'Recently'}
                   </p>
               </div>
           </div>

           {/* Settings */}
           <div className="space-y-6">
               <h2 className="text-lg font-medium text-white border-b border-white/10 pb-4">Account Settings</h2>

               {/* Upgrade Plan */}
               <div 
                   onClick={() => onOpenSubscription && onOpenSubscription()}
                   className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg hover:bg-zinc-900 transition-colors cursor-pointer border border-transparent hover:border-white/5"
               >
                   <div className="flex items-center gap-4">
                       <div className="p-2 bg-zinc-800 rounded-md text-zinc-400">
                           <ICONS.Zap className="w-5 h-5" />
                       </div>
                       <div>
                           <h4 className="text-sm font-medium text-white">Change Plan</h4>
                           <p className="text-xs text-zinc-500">Upgrade or downgrade subscription</p>
                       </div>
                   </div>
                   <ICONS.ChevronLeft className="w-4 h-4 rotate-180 text-zinc-600" />
               </div>

                {/* Manage Payments Link */}
                <a 
                   href="https://billing.stripe.com/p/login/dRmbJ34Jh0IP9GMalO1Jm00"
                   target="_blank"
                   rel="noopener noreferrer"
                   className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg hover:bg-zinc-900 transition-colors cursor-pointer border border-transparent hover:border-white/5"
                >
                   <div className="flex items-center gap-4">
                       <div className="p-2 bg-zinc-800 rounded-md text-zinc-400">
                           <ICONS.CreditCard className="w-5 h-5" />
                       </div>
                       <div>
                           <h4 className="text-sm font-medium text-white">Manage Payments</h4>
                           <p className="text-xs text-zinc-500">Update card, billing history</p>
                       </div>
                   </div>
                   <ICONS.ArrowRight className="w-4 h-4 text-zinc-600" />
               </a>
               
               {/* SUPPORT BUTTON */}
               <div 
                   onClick={() => setShowSupport(true)}
                   className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg hover:bg-zinc-900 transition-colors cursor-pointer border border-transparent hover:border-white/5"
               >
                   <div className="flex items-center gap-4">
                       <div className="p-2 bg-zinc-800 rounded-md text-zinc-400">
                           <ICONS.MessageSquare className="w-5 h-5" />
                       </div>
                       <div>
                           <h4 className="text-sm font-medium text-white">Help & Support</h4>
                           <p className="text-xs text-zinc-500">Contact us if you have questions</p>
                       </div>
                   </div>
                   <ICONS.ArrowRight className="w-4 h-4 text-zinc-600" />
               </div>

               {/* Legal Documents */}
               <div className="flex flex-col gap-2 mt-4 border-t border-white/5 pt-6">
                    <div 
                        onClick={() => setActiveLegalModal('TERMS')}
                        className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg hover:bg-zinc-900 transition-colors cursor-pointer border border-transparent hover:border-white/5"
                    >
                        <div className="flex items-center gap-4">
                            <div className="p-2 bg-zinc-800 rounded-md text-zinc-400">
                                <ICONS.BookOpen className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="text-sm font-medium text-white">Terms of Service</h4>
                                <p className="text-xs text-zinc-500">Usage rules and policies</p>
                            </div>
                        </div>
                        <ICONS.ArrowRight className="w-4 h-4 text-zinc-600" />
                    </div>
                    
                    <div 
                        onClick={() => setActiveLegalModal('PRIVACY')}
                        className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg hover:bg-zinc-900 transition-colors cursor-pointer border border-transparent hover:border-white/5"
                    >
                        <div className="flex items-center gap-4">
                            <div className="p-2 bg-zinc-800 rounded-md text-zinc-400">
                                <ICONS.Lock className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="text-sm font-medium text-white">Privacy Policy</h4>
                                <p className="text-xs text-zinc-500">How we handle your data</p>
                            </div>
                        </div>
                        <ICONS.ArrowRight className="w-4 h-4 text-zinc-600" />
                    </div>
               </div>
                
                {/* Sign Out */}
                <div 
                    onClick={() => logoutUser()}
                    className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg hover:bg-zinc-900 transition-colors cursor-pointer border border-transparent hover:border-white/5 mt-8"
                >
                   <div className="flex items-center gap-4">
                       <div className="p-2 bg-zinc-800 rounded-md text-zinc-400">
                           <ICONS.LogOut className="w-5 h-5" />
                       </div>
                       <div>
                           <h4 className="text-sm font-medium text-white">Sign Out</h4>
                           <p className="text-xs text-zinc-500">Securely log out of this session</p>
                       </div>
                   </div>
                   <ICONS.ArrowRight className="w-4 h-4 text-zinc-600" />
               </div>

                {/* Delete Account */}
                <div 
                    onClick={handleDeleteAccount}
                    className="flex items-center justify-between p-4 bg-red-950/10 rounded-lg hover:bg-red-900/20 group transition-colors cursor-pointer border border-red-900/20 hover:border-red-900/50"
                >
                   <div className="flex items-center gap-4">
                       <div className="p-2 bg-red-900/20 group-hover:bg-red-900/30 rounded-md text-red-500 group-hover:text-red-400 transition-colors">
                           <ICONS.Trash className="w-5 h-5" />
                       </div>
                       <div>
                           <h4 className="text-sm font-medium text-red-500 group-hover:text-red-400 transition-colors">Delete Account</h4>
                           <p className="text-xs text-red-500/60 group-hover:text-red-400/60 transition-colors">Permanently remove your data</p>
                       </div>
                   </div>
               </div>

               {/* --- ADMIN CONSOLE --- */}
               {userData?.isAdmin && (
                   <div className="mt-12 p-6 border border-red-900/50 rounded-xl bg-red-900/5">
                        <div className="flex items-center gap-3 mb-4">
                            <ICONS.Lock className="w-5 h-5 text-red-500" />
                            <h3 className="text-red-400 font-bold uppercase tracking-widest text-sm">Admin Console</h3>
                        </div>
                        
                        <div className="bg-black/40 p-4 rounded-lg border border-red-900/30">
                            <h4 className="text-xs text-zinc-400 uppercase font-bold mb-3">Create Promo Code</h4>
                            <form onSubmit={handleCreatePromo} className="flex gap-2">
                                <input 
                                    type="text" 
                                    placeholder="CODE"
                                    value={newPromoCode}
                                    onChange={(e) => setNewPromoCode(e.target.value)}
                                    className="flex-1 bg-black/50 border border-zinc-700 rounded px-3 py-2 text-sm text-white uppercase focus:border-red-500 focus:outline-none"
                                />
                                <input 
                                    type="number" 
                                    placeholder="RESHOOTS"
                                    value={newPromoValue}
                                    onChange={(e) => setNewPromoValue(Number(e.target.value))}
                                    className="w-24 bg-black/50 border border-zinc-700 rounded px-3 py-2 text-sm text-white focus:border-red-500 focus:outline-none"
                                />
                                <button 
                                    type="submit"
                                    disabled={creatingPromo}
                                    className="bg-red-900 hover:bg-red-800 text-white px-4 py-2 rounded text-xs font-bold uppercase tracking-widest disabled:opacity-50"
                                >
                                    {creatingPromo ? '...' : 'Create'}
                                </button>
                            </form>
                        </div>
                   </div>
               )}

               {/* TEMP DEV BUTTON (Remove in Production) */}
               <div className="pt-12 text-center">
                   <button 
                        onClick={toggleMyAdminStatus}
                        className="text-[9px] text-zinc-700 hover:text-red-500 font-mono uppercase cursor-pointer"
                    >
                        [Dev: Toggle Admin Status]
                    </button>
               </div>

           </div>
           
           {/* Render Legal Modal */}
           <LegalModal type={activeLegalModal} onClose={() => setActiveLegalModal(null)} />
           
           {/* Render Support Modal */}
           {showSupport && <SupportModal onClose={() => setShowSupport(false)} />}
       </div>
    </div>
  );
};

export default Profile;
